// SettingsPlanThresholdAlerts.jsx

import React, { useMemo } from 'react';

// CSS
import './SettingsPlanThresholdAlerts.css';

const LIMIT_LABELS = Object.freeze({
    api: 'API runs',
    live: 'live minutes',
    storage: 'storage',
    teamUsage: 'team usage',
});

/**
 * SettingsPlanThresholdAlerts
 *
 * Shows in-settings warnings when space usage crosses 80% or 100%.
 *
 * @returns {ReactNode} The rendered component.
 */
const SettingsPlanThresholdAlerts = React.memo(({
    alerts = [],
    theme,
}) => {

    // ----- MEMOS ----- //

    const visibleAlerts = useMemo(() => {

        return (Array.isArray(alerts) ? alerts : []).filter((alert) => alert?.limitType);
    }, [alerts]);

    if (!visibleAlerts.length) {
        return null;
    }
    // ----- RENDER ----- //

    return (
        <div className="settings-modal-plan-threshold-alerts">
            {visibleAlerts.map((alert) => {
                const limitLabel = LIMIT_LABELS[alert.limitType] || 'space usage';
                const isCritical = alert.severity === 'critical' || alert.threshold >= 100;
                const message = alert.threshold >= 100
                    ? `${limitLabel} limit reached (${alert.percent}% used). Uploads and other pool-backed actions may be blocked until you upgrade or free space.`
                    : `${limitLabel} is ${alert.percent}% full. Consider upgrading before your space hits its limit.`;

                return (
                    <div
                      className={`settings-modal-plan-threshold-alert ${isCritical ? 'is-critical' : 'is-warning'}`}
                      style={{
                            backgroundColor: theme.backgroundMuted,
                            borderColor: isCritical ? theme.warning : theme.backgroundTertiary,
                            color: theme.foreground,
                        }}
                      key={`${alert.limitType}-${alert.threshold}`}
                    >

                        {/* PLAN THRESHOLD ALERT TITLE */}
                        <div className="settings-modal-plan-threshold-alert-title">
                            {isCritical ? 'Limit reached' : 'Approaching limit'}
                        </div>

                        {/* PLAN THRESHOLD ALERT BODY */}
                        <div
                          className="settings-modal-plan-threshold-alert-body"
                        >
                            {message}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

// Dev Tools
SettingsPlanThresholdAlerts.displayName = 'SettingsPlanThresholdAlerts';

export default SettingsPlanThresholdAlerts;
